/**
 * Composable для управления пошаговым визардом «Собрать набор»
 */

import { ref, computed, type Ref } from 'vue';
import type { ProductBundle, ProductBundleStep, BundleStepProduct } from '@entities/product';
import { isMenuItemAvailable } from '@entities/product';

/**
 * Доступность товара шага — то же правило, что у слотов комбо: живёт в
 * `entities/product/lib/menuAvailability`, здесь только псевдоним под тип шага.
 */
const isBundleStepProductAvailable = (product: BundleStepProduct): boolean =>
  isMenuItemAvailable(product);

/**
 * @param baseAddonsSum доплата за добавки ОСНОВНОГО блюда, выбранные в карточке товара
 *   до сборки набора. Она уходит в корзину вместе с составом (`base_addons`), поэтому
 *   обязана быть и в цене на кнопке: иначе набор обещает одну сумму, а списывает другую.
 */
export function useBundleBuilder(baseAddonsSum: Ref<number> | (() => number) = () => 0) {
  const resolveBaseAddonsSum = (): number => {
    const raw = typeof baseAddonsSum === 'function' ? baseAddonsSum() : baseAddonsSum.value;
    return Number.isFinite(raw) ? Math.max(0, raw) : 0;
  };

  const activeBundle = ref<ProductBundle | null>(null);
  const currentStepIndex = ref(0);
  // step_id → product_id
  const selections = ref<Record<number, number>>({});
  const isOpen = ref(false);

  // ─── Вычисляемые свойства ───────────────────────────────────────────────────

  /** Текущий шаг визарда */
  const currentStep = computed<ProductBundleStep | null>(() =>
    activeBundle.value?.steps[currentStepIndex.value] ?? null,
  );

  /** ID выбранного товара на текущем шаге */
  const currentSelectedProductId = computed<number | null>(() => {
    if (!currentStep.value) return null;
    return selections.value[currentStep.value.id] ?? null;
  });

  /** Объект выбранного товара текущего шага (для смены картинки) */
  const currentSelectedProduct = computed<BundleStepProduct | null>(() => {
    if (!currentStep.value || !currentSelectedProductId.value) return null;
    return (
      currentStep.value.products.find(
        (p) => p.product_id === currentSelectedProductId.value,
      ) ?? null
    );
  });

  /**
   * Текущая цена набора:
   * base_price + доплата за добавки основного блюда + сумма price_delta по шагам.
   *
   * Добавки основного блюда обязаны входить в цену: они уезжают в корзину вместе с
   * набором (`base_addons`), и сервер их считает. Без них кнопка обещала «В корзину
   * 500 ₽», а в корзину ложилось 550 ₽ — ровно на стоимость выбранного бекона.
   */
  const calculatedPrice = computed<number>(() => {
    if (!activeBundle.value) return 0;
    const deltasSum = activeBundle.value.steps.reduce((sum, step) => {
      const pid = selections.value[step.id];
      if (!pid) return sum;
      const product = step.products.find((p) => p.product_id === pid);
      return sum + (product?.price_delta ?? 0);
    }, 0);
    return activeBundle.value.base_price + resolveBaseAddonsSum() + deltasSum;
  });

  /** Выбранный на шаге товар недоступен (или на обязательном шаге нечего выбрать) */
  const isSelectionUnavailable = (step: ProductBundleStep): boolean => {
    const pid = selections.value[step.id];
    if (!pid) return false;
    const product = step.products.find((p) => p.product_id === pid);
    return !!product && !isBundleStepProductAvailable(product);
  };

  /** На обязательном шаге не осталось ни одного доступного товара */
  const stepHasNoAvailableProducts = (step: ProductBundleStep): boolean =>
    step.is_required && !step.products.some(isBundleStepProductAvailable);

  /** Можно ли перейти к следующему шагу */
  const canProceed = computed<boolean>(() => {
    const step = currentStep.value;
    if (!step) return false;
    // Недоступный товар выбрать нельзя, но состав мог протухнуть между открытием
    // и нажатием — не пускаем дальше, иначе набор отобьётся уже на сервере.
    if (isSelectionUnavailable(step)) return false;
    if (!step.is_required) return true;
    if (stepHasNoAvailableProducts(step)) return false;
    return !!selections.value[step.id];
  });

  /** Набор целиком собран из доступных товаров — иначе кнопку «В корзину» блокируем */
  const isBundleAvailable = computed<boolean>(() => {
    const bundle = activeBundle.value;
    if (!bundle) return false;
    return !bundle.steps.some(
      (step) => isSelectionUnavailable(step) || stepHasNoAvailableProducts(step),
    );
  });

  /** Обязательный шаг, который нечем закрыть — набор собрать нельзя вообще */
  const blockedStep = computed<ProductBundleStep | null>(
    () => activeBundle.value?.steps.find(stepHasNoAvailableProducts) ?? null,
  );

  /** Находимся ли на последнем шаге */
  const isLastStep = computed<boolean>(() =>
    currentStepIndex.value === (activeBundle.value?.steps.length ?? 0) - 1,
  );

  // ─── Вспомогательные функции ────────────────────────────────────────────────

  /**
   * Форматирование price_delta:
   * 0 → "включено", >0 → "+7 ₽", <0 → "-91 ₽"
   */
  const formatPriceDelta = (delta: number): string => {
    if (delta === 0) return 'включено';
    if (delta > 0) return `+${delta} ₽`;
    return `${delta} ₽`;
  };

  /** Получить URL картинки товара шага (поддержка объекта и строки) */
  const getStepProductImage = (product: BundleStepProduct): string | null => {
    if (!product.img) return null;
    if (typeof product.img === 'string') return product.img;
    return product.img.url ?? null;
  };

  /** Получить картинку выбранного товара на шаге по индексу */
  const getSelectedProductImageForStep = (stepIndex: number): string | null => {
    if (!activeBundle.value) return null;
    const step = activeBundle.value.steps[stepIndex];
    if (!step) return null;
    const pid = selections.value[step.id];
    if (!pid) return null;
    const product = step.products.find((p) => p.product_id === pid);
    if (!product) return null;
    return getStepProductImage(product);
  };

  /** Статус шага: пройден / активен / ожидает */
  const getStepStatus = (stepIndex: number): 'done' | 'active' | 'pending' => {
    if (stepIndex < currentStepIndex.value) return 'done';
    if (stepIndex === currentStepIndex.value) return 'active';
    return 'pending';
  };

  // ─── Действия ───────────────────────────────────────────────────────────────

  /**
   * Инициализировать дефолтные значения при открытии бандла.
   *
   * Дефолт из стоп-листа не подставляем: набор открылся бы с заведомо недобавляемым
   * выбором и упёрся в заблокированную кнопку. Берём первый доступный товар шага.
   */
  const initDefaults = (bundle: ProductBundle) => {
    selections.value = {};
    for (const step of bundle.steps) {
      const defaultProduct = step.products.find((p) => p.is_default);
      const selected =
        defaultProduct && isBundleStepProductAvailable(defaultProduct)
          ? defaultProduct
          : step.products.find(isBundleStepProductAvailable);

      if (selected) {
        selections.value[step.id] = selected.product_id;
      }
    }
  };

  /** Открыть визард для конкретного бандла */
  const openBundle = (bundle: ProductBundle) => {
    activeBundle.value = bundle;
    currentStepIndex.value = 0;
    initDefaults(bundle);
    isOpen.value = true;
  };

  /** Закрыть визард и сбросить состояние */
  const close = () => {
    isOpen.value = false;
    activeBundle.value = null;
    selections.value = {};
    currentStepIndex.value = 0;
  };

  /**
   * Выбрать товар на шаге. Недоступный игнорируем и сообщаем об этом вызывающему —
   * оверлей показывает тост, чтобы отказ не выглядел как «кнопка не нажалась».
   */
  const selectProduct = (stepId: number, productId: number): boolean => {
    const step = activeBundle.value?.steps.find((s) => s.id === stepId);
    const product = step?.products.find((p) => p.product_id === productId);

    if (product && !isBundleStepProductAvailable(product)) return false;

    selections.value[stepId] = productId;
    return true;
  };

  /** Перейти к следующему шагу (если разрешено) */
  const goNext = () => {
    if (canProceed.value && !isLastStep.value) {
      currentStepIndex.value++;
    }
  };

  /** Вернуться на шаг назад */
  const goBack = () => {
    if (currentStepIndex.value > 0) {
      currentStepIndex.value--;
    }
  };

  /** Перейти к конкретному шагу (только к уже пройденным) */
  const goToStep = (index: number) => {
    if (index <= currentStepIndex.value && index >= 0) {
      currentStepIndex.value = index;
    }
  };

  /**
   * Собрать payload для POST /cart/bundle
   */
  /**
   * @param baseModifiers состав основного блюда, выбранный ДО сборки набора: гость
   * настраивает размер и добавки в карточке товара и ждёт, что набор соберётся именно
   * с ними. Без этого в корзину уезжало голое блюдо.
   */
  const getCartPayload = (
    branchId: number,
    baseProductId: number,
    baseModifiers?: { addons?: (string | number)[], excludes?: (string | number)[] },
  ) => {
    if (!activeBundle.value) return null;
    return {
      branch_id: branchId,
      base_product_id: baseProductId,
      bundle_id: activeBundle.value.id,
      step_selections: Object.entries(selections.value).map(([stepId, productId]) => ({
        step_id: Number(stepId),
        product_id: Number(productId),
      })),
      base_addons: (baseModifiers?.addons ?? []).map(String),
      base_excludes: (baseModifiers?.excludes ?? []).map(String),
    };
  };

  return {
    // State
    activeBundle,
    currentStepIndex,
    selections,
    isOpen,

    // Computed
    currentStep,
    currentSelectedProductId,
    currentSelectedProduct,
    calculatedPrice,
    canProceed,
    isLastStep,
    isBundleAvailable,
    blockedStep,

    // Helpers
    formatPriceDelta,
    getStepProductImage,
    getSelectedProductImageForStep,
    getStepStatus,

    // Actions
    openBundle,
    close,
    selectProduct,
    goNext,
    goBack,
    goToStep,
    getCartPayload,
  };
}
